import { useCallback, useContext } from 'react';

import invariant from 'tiny-invariant';

import { ConfigContext } from './ConfigContext';
import { HandlersContext } from './HandlersContext';
import type { ItemId } from './drag-and-drop.types';

/**
 * hook to access drag-drop context values
 */
export function useDragAndDropContext() {
  const config = useContext(ConfigContext);
  const handlers = useContext(HandlersContext);

  invariant(
    config && handlers,
    'useDragAndDropContext must be used within a DragAndDropProvider. ' +
      'Ensure your component is wrapped with <DragAndDropProvider>.'
  );

  return { ...config, ...handlers };
}

/**
 * Hook for accessing only stable values (avoids re-renders from handler changes)
 */
export function useDragAndDropConfig() {
  const config = useContext(ConfigContext);
  invariant(config, 'useDragAndDropConfig must be used within a DragAndDropProvider');
  return config;
}

/**
 * Hook for accessing drag direction
 */
export function useDragDirection() {
  const config = useContext(ConfigContext);
  invariant(config, 'useDragDirection must be used within a DragAndDropProvider');
  return config.direction;
}

/**
 * Hook for accessing handlers (will re-render on handler changes)
 */
export function useDragAndDropHandlers() {
  const handlers = useContext(HandlersContext);
  invariant(handlers, 'useDragAndDropHandlers must be used within a DragAndDropProvider');
  return {
    onReorder: handlers.onReorder,
    onError: handlers.onError,
  };
}

/**
 * Hook for accessing validation functions
 */
export function useDragAndDropValidation() {
  const handlers = useContext(HandlersContext);
  invariant(handlers, 'useDragAndDropValidation must be used within a DragAndDropProvider');
  return {
    canDrop: handlers.canDrop,
    canDrag: handlers.canDrag,
  };
}

/**
 * Simplified announcements hook
 */
export function useDragAndDropAnnouncements() {
  const config = useContext(ConfigContext);
  const handlers = useContext(HandlersContext);

  invariant(
    config && handlers,
    'useDragAndDropAnnouncements must be used within a DragAndDropProvider'
  );

  const { announceToScreenReader } = config;

  const announceStart = useCallback(
    (itemId: ItemId, index: number) => {
      const message = handlers.announcements?.onDragStart
        ? handlers.announcements.onDragStart(itemId, index)
        : `Started dragging item ${index + 1}`;
      announceToScreenReader(message);
    },
    [handlers.announcements, announceToScreenReader]
  );

  const announceEnd = useCallback(
    (itemId: ItemId, oldIndex: number, newIndex: number) => {
      const message = handlers.announcements?.onDragEnd
        ? handlers.announcements.onDragEnd(itemId, oldIndex, newIndex)
        : `Moved item from position ${oldIndex + 1} to position ${newIndex + 1}`;
      announceToScreenReader(message);
    },
    [handlers.announcements, announceToScreenReader]
  );

  const announceCancel = useCallback(
    (itemId: ItemId, index: number) => {
      const message = handlers.announcements?.onDragCancel
        ? handlers.announcements.onDragCancel(itemId, index)
        : `Drag cancelled. Item returned to position ${index + 1}`;
      announceToScreenReader(message);
    },
    [handlers.announcements, announceToScreenReader]
  );

  return { announceStart, announceEnd, announceCancel };
}
